import { api } from '@/api'
import { LOCAL_STORAGE_CASE_NUMBER, LOCAL_STORAGE_OIL_SUPPLY } from '../../data/localStorage'
import theme from '@/plugins/theme'

import { epoch2timestamp, epoch2date, epoch2fullDate, isToday } from '@/utils/time'
import { normalizeSearchQueries, matchAll } from '@/utils/helpers'
import availableHeaders from './data/availableHeaders'

export default {
  // 保存しているデータとタイマーをクリアする。
  clearData ({ commit, dispatch }) {
    dispatch('clearTimer')
    commit('setArea', null)
    commit('setCustomers', [])
    commit('setSelectedIDs', [])
    commit('setHeaders', [])
    commit('setLoading', true)
    commit('setSearchText', '')
  },

  // 「データ取得時間」を現在時刻にセットし、１分ごとに「現在時刻」をアップデートするインターバルを開始する。
  startTimer ({ state, commit, dispatch }) {
    // 既にタイマーが存在していればクリアする。
    if (state.intervalId) {
      dispatch('clearTimer')
    }
    commit('setCurrentTimestamp', new Date().getTime())
    commit('setFetchedTimestamp', new Date().getTime())
    commit('setIntervalId', setInterval(() => {
      commit('setCurrentTimestamp', new Date().getTime())
    }, 1000 * 60))
  },
  // タイマーをクリアする。
  clearTimer ({ state, commit }) {
    clearInterval(state.intervalId)
    commit('setIntervalId', null)
    commit('setCurrentTimestamp', null)
    commit('setFetchedTimestamp', null)
  },

  // テーブルのヘッダーを取得。ローカルで更新する場合はorderを渡す。
  async getHeaders ({ state, commit }, order = false) {
    if (!order) {
      const data = await api.getCompanyConfigDeliveryList()
      order = data.order
    }
    commit('setHeaders', [])
    order.forEach(e => {
      commit('setHeaders', [...state.headers, availableHeaders[e - 1]])
    })
  },

  async updateHeaders ({ dispatch }, order) {
    await api.updateCompanyConfigDeliveryList({ order })
    dispatch('getHeaders', order)
  },

  setArea ({ commit, dispatch }, areaId) {
    commit('setArea', areaId)
    dispatch('setCustomers', { areaId })
  },

  // areaIdで指定したエリアの顧客を取得するリクエストを発行しVuexにセットする。
  // initがtrueの場合チェック済みの顧客と検索フィールドもリセットされる。
  async setCustomers ({ state, commit, dispatch }, { areaId = state.area, init = true } = {}) {
    if (init) {
      commit('setLoading', true)
      commit('setSearchText', '')
    }
    const response = await api.getDeliveryList(areaId)

    const customers = arrangeCustomersData(response.customers)

    dispatch('startTimer')
    commit('setCustomers', customers)
    if (init) {
      dispatch('initSelectedIDs')
    }
    dispatch('search')
    commit('setLoading', false)
  },
  toggleSelectedIDs ({ state, commit }, customer) {
    const id = customer.id
    const index = state.selectedIDs.indexOf(id)

    if (index >= 0) {
      commit('deSelectID', index)
      return
    }

    commit('selectID', { index, id })
  },
  initSelectedIDs ({ state, dispatch, commit }) {
    commit('setSelectedIDs', [])
    state.customers.forEach(e => {
      if (e.deliveryDate.status <= 0) {
        dispatch('toggleSelectedIDs', e)
      }
    })
  },

  search ({ state, getters, commit }, text = '') {
    let customers = JSON.parse(JSON.stringify(state.customers))
    // Initial render
    if (!(text + state.searchText)) {
      commit('setSearchResults', customers)
      return
    }

    commit('setLoading', true)
    commit('setSearchText', text)

    // Filter if search text contains a special string
    if (getters.isFilteredBySensorWarning) {
      customers = getters.customersWithSensorWarning
      text = text.replaceAll(state.specialString, '').trim()
    }

    // if text is empty skip so that the filtered result will be committed.
    if (text) {
      const queryTexts = normalizeSearchQueries(text)
      const targetFields = ['name', 'address', 'code', 'tel', 'remarks']
      customers = customers.filter(customer => {
        return queryTexts.every(query => matchAll(customer, targetFields, query))
      })
    }
    commit('setSearchResults', customers)
    setTimeout(() => commit('setLoading', false), 300)
  },

  setCaseNumberInput ({ commit }, data) {
    commit('setCaseNumberInput', data)
    localStorage.setItem(LOCAL_STORAGE_CASE_NUMBER, data)
  },
  setOilSupplyInput ({ commit }, data) {
    commit('setOilSupplyInput', data)
    localStorage.setItem(LOCAL_STORAGE_OIL_SUPPLY, data)
  }
}

/**
 * HELPER FUNCTIONS
 */
const MAX_DAYS = 6
const RED_THRESHOLD = 2
const YELLOW_THRESHOLD = 5

/**
 * タンクの配列を渡すと総給油可能量（タンクの空きの合計）を返す。
 * @param {{ oilMaxVolume: Number }[]} arr
 * @return {Number} タンクの空きの合計。
 */
const getOilingCapacity = arr => arr.reduce((acc, el) => (acc += (el.oilMaxVolume - el.oilVolume)), 0)

/**
 * 補給猶予を渡すと設定した値に参照してCSS用のオブジェクトを返す。
 * @param {Number} margin 補給猶予（単位：日）
 */
const styleOf = (margin) => {
  let color = theme.success
  if (margin <= RED_THRESHOLD) {
    color = theme.error
  } else if (margin <= YELLOW_THRESHOLD) {
    color = theme.warning
  }

  return {
    'background-color': color,
    'color': '#FFFFFF'
  }
}

/**
 * 補給猶予を渡すと設定した値に参照して表示用のテキストを返す。
 * @param {Number} margin 補給猶予（単位：日）
 */
const time2string = margin => {
  if (margin <= 1) {
    return '今日中'
  } else if (margin > MAX_DAYS) {
    return `${MAX_DAYS + 1}日以上`
  } else {
    return margin + '日以内'
  }
}

/**
 * APIから得た顧客データを使用に即した形に整えて返す。
 * @param {Array} customers
 * @return {Array}
 */
const arrangeCustomersData = (customers) =>
  customers.map(c => {
    const styleMargin = marginInSec => {
      const marginInDay = Math.ceil(Math.round(marginInSec) / (60 * 60 * 24))
      return {
        style: styleOf(marginInDay),
        label: time2string(marginInDay)
      }
    }

    let hasSensorWarning = false

    /**
     * タンクに設置されたセンサーの状態を返す。
     * @param {object} tank
     * @returns {string} 'none' | 'ok' | 'warning'
     */
    const getStateByUpdatedAt = (customer, { serialNo, volumeUpdatedAt }) => {
      if (!serialNo) return 'none'

      const THRESHOLD = 60 * 60 * 24 * 1 // 3 days
      const elapsed = (Date.now() / 1000) - volumeUpdatedAt

      // センサーを持っているが更新時刻を持っていない場合（未通信含む）も欠損扱いにする
      const virgin = serialNo && !volumeUpdatedAt

      const state = elapsed >= THRESHOLD || virgin
        ? 'warning'
        : 'ok'

      if (state === 'warning') {
        hasSensorWarning = true
      }

      return state
    }

    const tanks = c.tanks || []
    const tank = tanks.find(t => t.id === c.priorTankId)
    let lastSupplyDate = null
    let lastSupplyAmount = null
    if (c.latestSupply) {
      lastSupplyDate = c.latestSupply.timestamp ? epoch2fullDate(c.latestSupply.timestamp) : null
      lastSupplyAmount = Math.round(c.latestSupply.amount)
    }

    const customer = {
      id: c.id,
      code: c.code,
      name: c.name,
      address: c.address,
      tel: c.tel || '',
      legalMax: tank.tankMaxVolume,
      oilingCapacity: getOilingCapacity(tanks),
      remarks: c.remarks,
      volumeThreshold: tank.volumeThreshold,
      rest: restOrFull(tank.oilVolume, tank.tankMaxVolume),
      volumeUpdatedAt: tank.volumeUpdatedAt ? epoch2timestamp(tank.volumeUpdatedAt) : '-',
      margin: styleMargin(tank.volumeMargin),
      serialNo: tank.serialNo,
      topTankId: tank.id,
      deliveryFrequency: tank.deliveryFrequency || '-',
      lastSuppliedAt: lastSupplyDate || '未取得',
      lastSuppliedAmount: lastSupplyAmount || '-',
      deliveryDate: setDeliveryDate(c.latestAppointment),
      location: c.location,
      tanks: (tanks).map(t => {
        return {
          id: t.id,
          tankType: t.tankType,
          volumeThreshold: t.volumeThreshold || 0,
          volumeUpdatedAt: t.volumeUpdatedAt ? epoch2timestamp(t.volumeUpdatedAt) : null,
          installStatus: t.installStatus,
          activateStatus: t.activateStatus,
          consumptionRate: t.consumptionRate,
          rest: restOrFull(t.oilVolume, t.tankMaxVolume),
          margin: styleMargin(tank.volumeMargin),
          serialNo: t.serialNo,
          legalMax: t.tankMaxVolume,
          actualMax: t.oilMaxVolume,
          deliveryFrequency: t.deliveryFrequency,
          sensorState: getStateByUpdatedAt(c, t)
        }
      })
    }

    customer.hasSensorWarning = hasSensorWarning

    return customer
  })

/**
 *
 * @param {{ date: Number, name: String, reason: String }} deliveryDate
 * @return {{ date: String, name: String, reason: String, status: Number } | {}} status === -1: past | 0: today | 1: future
 */
const setDeliveryDate = ({ time, userName, reason } = {}) => {
  if (!time) return {}
  let date, status
  if (isToday(time)) {
    date = '今日'
    status = 0
  } else {
    date = epoch2date(time)
    status = time > new Date().getTime() / 1000
      ? 1
      : -1
  }
  return { date, name: userName, reason, status }
}

const restOrFull = (rest, max) => max && rest >= max ? '満タン' : rest
